import { addDays, differenceInCalendarDays, isValid, parseISO, startOfDay } from "date-fns"
import { formatOrderTableDate } from "@/lib/order-table-utils"

const CONNECTION_STATUS_MAP: Record<number, string> = {
  0: "Created",
  1: "Pending Approval",
  2: "Approved by Client",
  3: "Complete",
  4: "Declined by Client",
  5: "Bad Apple",
  6: "Reserve",
}

const STATUS_STRING_TO_NUM: Record<string, number> = {
  created: 0,
  pending_client_approval: 1,
  "pending client approval": 1,
  "pending approval": 1,
  approved_by_client: 2,
  complete: 3,
  completed: 3,
  fulfilled: 3,
  declined_by_client: 4,
  bad_apple: 5,
  reserve: 6,
}

/** Same numeric stage as used for filters and UI (0–5, plus 6 reserve); works with API string or number `status`. */
export function getConnectionStatusStageNum(conn: any): number | null {
  const s = conn?.status
  if (s === undefined || s === null || s === "" || String(s) === "n/a") return null
  if (typeof s === "number" && s >= 0 && s <= 6) return s
  const str = String(s).toLowerCase().trim()
  const fromMap = STATUS_STRING_TO_NUM[str]
  if (fromMap !== undefined) return fromMap
  const n = Number.parseInt(str, 10)
  return !Number.isNaN(n) && n >= 0 && n <= 6 ? n : null
}

export const LIVE_STATUSES = ["active", "target_url_wrong", "anchor_text_wrong", "url_and_anchor_wrong"]
export const BROKEN_STATUSES = ["failed", "target_url_wrong", "anchor_text_wrong", "url_and_anchor_wrong"]

export function getLinkDisplayLabel(urlCheck: { status?: string | null; previous_status?: string | null } | null | undefined): "Live" | "Link Down" | "Not Live" {
  const status = (urlCheck?.status ?? "").toLowerCase()
  const previous = (urlCheck?.previous_status ?? "").toLowerCase()
  if (LIVE_STATUSES.includes(status)) return "Live"
  if (previous === "active" && BROKEN_STATUSES.includes(status)) return "Link Down"
  return "Not Live"
}

export const connectionStatusStyles: Record<string, string> = {
  Created: "bg-slate-100 text-slate-800 border-slate-200",
  "Pending Approval": "bg-amber-100 text-amber-800 border-amber-200",
  "Approved by Client": "bg-blue-100 text-blue-800 border-blue-200",
  Complete: "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Declined by Client": "bg-red-100 text-red-800 border-red-200",
  "Bad Apple": "bg-red-900 text-red-100 border-red-800",
  Reserve: "bg-violet-100 text-violet-800 border-violet-200",
}

/** ISO date string for the history row where status went from Created (0) to Pending Approval (1). No other source. */
export function getPendingClientApprovalHistoryDateIso(conn: any): string | null {
  const history = conn?.history
  if (!Array.isArray(history)) return null
  const matches: string[] = []
  for (const entry of history) {
    const changes = entry?.changes
    if (!Array.isArray(changes)) continue
    const hasTransition = changes.some(
      (c: any) =>
        c?.field === "status" && Number(c?.old) === 0 && Number(c?.new) === 1
    )
    if (!hasTransition || entry?.date == null) continue
    const ds = String(entry.date).trim()
    if (ds) matches.push(ds)
  }
  if (matches.length === 0) return null
  matches.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
  return matches[matches.length - 1]!
}

/**
 * When `history` is missing on list payloads, fall back to `added_time` (same anchor the UI often uses for “sent to client”).
 */
export function getPendingApprovalEffectiveSinceIso(conn: any): string | null {
  const fromHistory = getPendingClientApprovalHistoryDateIso(conn)
  if (fromHistory) return fromHistory
  const added = conn?.added_time != null ? String(conn.added_time).trim() : ""
  return added || null
}

export interface ConnectionStatusHistoryEntry {
  status: string
  dateRaw: string
}

function compareConnectionStatusHistoryByDate(
  a: { dateRaw: string },
  b: { dateRaw: string }
): number {
  const ta = new Date(String(a.dateRaw ?? "").trim()).getTime()
  const tb = new Date(String(b.dateRaw ?? "").trim()).getTime()
  const va = Number.isNaN(ta) ? null : ta
  const vb = Number.isNaN(tb) ? null : tb
  if (va === null && vb === null) return 0
  if (va === null) return 1
  if (vb === null) return -1
  return va - tb
}

function connectionHistoryEntryIncludesStatusChange(entry: any): boolean {
  const changes = entry?.changes
  if (!Array.isArray(changes)) return false
  if (!changes.some((c: any) => c?.field === "status")) return false
  const cf = entry?.changed_fields
  if (cf === undefined || cf === null) return true
  if (!Array.isArray(cf) || cf.length === 0) return true
  return cf.some((f: any) => String(f).toLowerCase() === "status")
}

function dedupeConnectionStatusHistory(
  rows: ConnectionStatusHistoryEntry[]
): ConnectionStatusHistoryEntry[] {
  const seen = new Set<string>()
  const out: ConnectionStatusHistoryEntry[] = []
  for (const r of rows) {
    const k = `${r.status}\0${r.dateRaw}`
    if (seen.has(k)) continue
    seen.add(k)
    out.push(r)
  }
  return out
}

function parseHistoryStatusRows(conn: any): ConnectionStatusHistoryEntry[] {
  const history = conn?.history
  if (!Array.isArray(history)) return []
  const rows: ConnectionStatusHistoryEntry[] = []
  for (const entry of history) {
    if (entry == null || entry.date == null) continue
    if (!connectionHistoryEntryIncludesStatusChange(entry)) continue
    const changes = entry.changes
    const sc = Array.isArray(changes)
      ? changes.find((c: any) => c?.field === "status")
      : null
    if (!sc) continue
    const newN = Number(sc.new)
    if (Number.isNaN(newN) || newN < 0 || newN > 6) continue
    // Created (0) is only shown from added_time in buildConnectionStatusHistoryEntries — never from
    // history rows (avoids duplicate "Created" from legacy/migration x→0 noise).
    if (newN === 0) continue
    const label = CONNECTION_STATUS_MAP[newN]
    if (!label) continue
    const oldN = Number(sc.old)
    if (
      Number.isFinite(oldN) &&
      Number.isFinite(newN) &&
      oldN === newN
    ) {
      continue
    }
    const ds = String(entry.date).trim()
    if (!ds) continue
    rows.push({ status: label, dateRaw: ds })
  }
  return rows
}

function getConnectionCreatedDateRaw(conn: any): string {
  const createdDate =
    conn?.added_time ??
    conn?.date_added ??
    conn?.created_at ??
    conn?.date_created ??
    ""
  return createdDate ? String(createdDate).trim() : ""
}

function buildConnectionStatusHistoryFallback(conn: any): ConnectionStatusHistoryEntry[] {
  const statusHistoryEntries: ConnectionStatusHistoryEntry[] = []
  const createdStr = getConnectionCreatedDateRaw(conn)
  if (createdStr) {
    statusHistoryEntries.push({ status: "Created", dateRaw: createdStr })
  }
  const stageNumForPending = getConnectionStatusStageNum(conn)
  if (stageNumForPending !== null && stageNumForPending >= 1) {
    const pendingApprovalIso = getPendingApprovalEffectiveSinceIso(conn)
    statusHistoryEntries.push({
      status: "Pending Approval",
      dateRaw: pendingApprovalIso ?? "",
    })
  }
  if (conn?.approved_time) {
    statusHistoryEntries.push({
      status: "Approved by Client",
      dateRaw: String(conn.approved_time),
    })
  }
  if (conn?.fulfilled_time) {
    statusHistoryEntries.push({
      status: "Complete",
      dateRaw: String(conn.fulfilled_time),
    })
  }
  if (conn?.reject_time) {
    statusHistoryEntries.push({
      status: "Declined by Client",
      dateRaw: String(conn.reject_time),
    })
  }
  if (getConnectionStatusStageNum(conn) === 5 && conn?.stage_updated_date) {
    statusHistoryEntries.push({
      status: "Bad Apple",
      dateRaw: String(conn.stage_updated_date),
    })
  }
  return dedupeConnectionStatusHistory(statusHistoryEntries).sort(
    compareConnectionStatusHistoryByDate
  )
}

/**
 * Timeline for connection details: when `history` has status `changes`, uses each entry's `date` and `new` status
 * (skipping transitions to status 0 — Created is only from `added_time`). Otherwise falls back to timestamp fields.
 */
export function buildConnectionStatusHistoryEntries(
  conn: any
): ConnectionStatusHistoryEntry[] {
  const fromHistory = parseHistoryStatusRows(conn)
  if (fromHistory.length > 0) {
    const out: ConnectionStatusHistoryEntry[] = []
    const createdStr = getConnectionCreatedDateRaw(conn)
    if (createdStr) {
      out.push({ status: "Created", dateRaw: createdStr })
    }
    out.push(...fromHistory)
    return dedupeConnectionStatusHistory(out).sort(compareConnectionStatusHistoryByDate)
  }
  return buildConnectionStatusHistoryFallback(conn)
}

function parseConnectionDateString(iso: string): Date | null {
  const s = String(iso).trim()
  if (!s) return null
  const parsed = parseISO(s)
  if (isValid(parsed)) return parsed
  const fallback = new Date(s)
  return Number.isNaN(fallback.getTime()) ? null : fallback
}

/** Calendar days after entering pending approval until auto-approve; FE-only until BE runs the job. */
export const AUTO_APPROVE_AFTER_PENDING_DAYS = 7

/**
 * Subline under Status for pending-approval rows: full calendar days from today until
 * start-of-day(pending-since) + AUTO_APPROVE_AFTER_PENDING_DAYS (matches “7 days from the day you went pending”).
 */
export function formatAutoApproveInDaysLabel(pendingSinceIso: string | null): string {
  if (pendingSinceIso == null || String(pendingSinceIso).trim() === "") return "—"
  const start = parseConnectionDateString(String(pendingSinceIso).trim())
  if (!start) return "—"
  const pendingDay = startOfDay(start)
  const deadlineDay = addDays(pendingDay, AUTO_APPROVE_AFTER_PENDING_DAYS)
  const today = startOfDay(new Date())
  const daysLeft = Math.max(0, differenceInCalendarDays(deadlineDay, today))
  if (daysLeft === 1) return "AUTO APPROVE IN 1 DAY"
  return `AUTO APPROVE IN ${daysLeft} DAYS`
}

/** True for the pending-approval countdown subline under Status (not "—"). */
export function isAutoApproveCountdownLabel(text: string): boolean {
  return String(text ?? "").trim().startsWith("AUTO APPROVE")
}

export function getConnectionStatusDisplay(conn: any): string {
  const s = conn?.status
  if (s === undefined || s === null || s === "" || String(s) === "n/a") return "—"
  let n: number
  if (typeof s === "number") {
    n = s
  } else {
    const str = String(s).toLowerCase().trim()
    n = STATUS_STRING_TO_NUM[str] ?? Number.parseInt(str, 10)
  }
  if (Number.isNaN(n) || n < 0 || n > 6) return "—"
  return CONNECTION_STATUS_MAP[n] ?? String(n)
}

function pickNumericMetric(v: unknown): number | null {
  if (v == null || v === "") return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

export function formatBenefitEntry(b: unknown): string {
  if (typeof b === "string") return b.trim()
  if (b && typeof b === "object") {
    const o = b as Record<string, unknown>
    if (typeof o.value === "string" && o.value.trim()) return o.value.trim()
    if (typeof o.content === "string" && o.content.trim()) return o.content.trim()
    if (typeof o.name === "string" && o.name.trim()) return o.name.trim()
  }
  const s = String(b ?? "").trim()
  return s
}

/** Matches fallback used in connection-details-modal when API sends no benefit list. */
const LEVEL_FALLBACK_BENEFITS: Record<string, string[]> = {
  Touchdown: ["Logo on event banner", "Social media mention", "Website listing"],
  Major: ["Logo on event banner", "Social media mention", "Website listing", "VIP passes (2)"],
  Friend: ["Website listing", "Thank you mention"],
  Roulette: ["Logo on event materials", "Social media shoutout", "VIP passes (4)"],
  Premier: ["Premier logo placement", "Speaking opportunity", "VIP passes (6)", "Booth space"],
}

/** Benefits whose text mentions both logo and link (e.g. "Logo & link exposure") sort before the rest. */
export function sortBenefitLinesLogoLinkFirst(lines: string[]): string[] {
  const primary: string[] = []
  const rest: string[] = []
  for (const line of lines) {
    const n = line.toLowerCase()
    if (n.includes("logo") && n.includes("link")) primary.push(line)
    else rest.push(line)
  }
  return [...primary, ...rest]
}

function getBenefitLinesForConnection(conn: any): string[] {
  const apiLevel = Array.isArray(conn?.sponsorship_level?.benefits) ? conn.sponsorship_level.benefits : []
  const fromLevel = apiLevel.map(formatBenefitEntry).filter(Boolean)
  if (fromLevel.length) return sortBenefitLinesLogoLinkFirst(fromLevel)
  const apiOpp = Array.isArray(conn?.sponsorship_level?.opp?.benefits) ? conn.sponsorship_level.opp.benefits : []
  const fromOpp = apiOpp.map(formatBenefitEntry).filter(Boolean)
  if (fromOpp.length) return sortBenefitLinesLogoLinkFirst(fromOpp)
  const levelName = String(conn?.sponsorship_level?.name ?? "").trim()
  if (levelName && LEVEL_FALLBACK_BENEFITS[levelName])
    return sortBenefitLinesLogoLinkFirst([...LEVEL_FALLBACK_BENEFITS[levelName]])
  return []
}

/**
 * SEO fields only from `sponsorship_level.opp.org` on the connection payload (`da`, `dr`, `traffic`, `spam_score`).
 * No fallbacks to opp, target_url, url_check, or da_max / domain_authority.
 */
export function getSeoMetricsForConnection(conn: any): {
  seoDa: number | null
  seoDr: number | null
  seoTraffic: string | null
  seoSpam: number | null
} {
  const org = conn?.sponsorship_level?.opp?.org
  const seoDa = pickNumericMetric(org?.da)
  const seoDr = pickNumericMetric(org?.dr)
  const seoSpam = pickNumericMetric(org?.spam_score)
  const trafficRaw = org?.traffic
  let seoTraffic: string | null = null
  if (trafficRaw != null && trafficRaw !== "") {
    seoTraffic = typeof trafficRaw === "number" ? trafficRaw.toLocaleString() : String(trafficRaw).trim()
  }
  return { seoDa, seoDr, seoTraffic, seoSpam }
}

export function formatConnectionForDisplay(conn: any, _zsOppFee?: number) {
  const org = conn?.sponsorship_level?.opp?.org?.name ?? conn?.org?.name ?? "—"
  const opportunity = conn?.sponsorship_level?.opp?.name ?? conn?.sponsorship_level?.name ?? conn?.opportunity ?? "—"
  const status = getConnectionStatusDisplay(conn)
  const sponsorshipNum =
    conn?.sponsorship_cost != null && typeof conn.sponsorship_cost === "number"
      ? conn.sponsorship_cost
      : 0
  const processingFeeNum =
    conn?.processing_fee != null && typeof conn.processing_fee === "number"
      ? conn.processing_fee
      : conn?.processing_fee_percent != null && typeof conn.processing_fee_percent === "number" && sponsorshipNum > 0
        ? (sponsorshipNum * conn.processing_fee_percent) / 100
        : 0
  const zsFeeNum =
    conn?.sponsorship_fee != null && typeof conn.sponsorship_fee === "number"
      ? conn.sponsorship_fee
      : 0
  const total = sponsorshipNum + processingFeeNum + zsFeeNum
  const costFormatted = total > 0 ? `$${total.toLocaleString()}` : "—"
  const stageNum = getConnectionStatusStageNum(conn)
  let statusDate: string
  if (stageNum === 1) {
    statusDate = formatAutoApproveInDaysLabel(getPendingApprovalEffectiveSinceIso(conn))
  } else if (stageNum === 2) {
    const approvedTimeStr =
      conn?.approved_time != null ? String(conn.approved_time).trim() : ""
    if (!approvedTimeStr) {
      statusDate = "—"
    } else {
      const d = new Date(approvedTimeStr)
      statusDate = Number.isNaN(d.getTime()) ? "—" : formatOrderTableDate(approvedTimeStr)
    }
  } else if (stageNum === 4) {
    const rejectTimeStr = conn?.reject_time != null ? String(conn.reject_time).trim() : ""
    if (!rejectTimeStr) {
      statusDate = "—"
    } else {
      const d = new Date(rejectTimeStr)
      statusDate = Number.isNaN(d.getTime()) ? "—" : formatOrderTableDate(rejectTimeStr)
    }
  } else {
    const rawStatusDate =
      stageNum === 0
        ? conn?.added_time ?? conn?.stage_updated_date ?? conn?.status_date ?? conn?.date_updated ?? conn?.date_added ?? conn?.created_at ?? ""
        : conn?.stage_updated_date ?? conn?.status_date ?? conn?.date_updated ?? ""
    const statusDateRaw = String(rawStatusDate).trim()
    statusDate = statusDateRaw
      ? (() => {
          const d = new Date(statusDateRaw)
          return Number.isNaN(d.getTime()) ? statusDateRaw : formatOrderTableDate(statusDateRaw)
        })()
      : "—"
  }
  const comboAddr = conn?.combo?.address
  const address =
    comboAddr && typeof comboAddr === "object"
      ? [comboAddr.city, comboAddr.state].filter(Boolean).join(", ") || "—"
      : "—"
  const sourceUrl = conn?.thank_you_url ?? conn?.sponsorship_level?.opp?.thank_you_url ?? ""
  const targetUrl = conn?.combo?.target_url_text ?? ""
  const anchorRaw = conn?.combo?.anchor_text_value ?? "—"
  const anchor = anchorRaw && anchorRaw !== "—" && String(anchorRaw).toLowerCase() !== "<img>"
    ? `# ${String(anchorRaw).trim()}`
    : anchorRaw
  const paid = (conn?.payments?.[0]?.status ?? "") === "paid"
  const paidHistoryEntry = paid
    ? (conn?.payments?.[0]?.history ?? []).find((h: any) =>
        Array.isArray(h?.changes) &&
        h.changes.some((c: any) => c?.field === "status" && c?.new === "paid"),
      )
    : null
  const paidDateRaw = paidHistoryEntry?.date ? String(paidHistoryEntry.date).trim() : ""
  const paidDate = paidDateRaw
    ? (() => {
        const d = new Date(paidDateRaw)
        return Number.isNaN(d.getTime()) ? paidDateRaw : formatOrderTableDate(paidDateRaw)
      })()
    : ""
  const PAYMENT_TYPE_LABELS: Record<string, string> = {
    credit_card: "Credit Card",
    paypal: "Paypal",
    check: "Check",
  }
  const paymentTypeRaw = conn?.payments?.[0]?.payment_method?.type ?? ""
  const method = paymentTypeRaw ? (PAYMENT_TYPE_LABELS[paymentTypeRaw] ?? paymentTypeRaw) : "—"
  const hasUrlCheck = conn?.url_check != null && conn?.url_check?.status != null && String(conn.url_check.status).trim() !== ""
  const urlCheckStatus = (conn?.url_check?.status ?? "").toLowerCase()
  const urlCheckPreviousStatus = (conn?.url_check?.previous_status ?? "").toLowerCase()
  const linkLive = hasUrlCheck && LIVE_STATUSES.includes(urlCheckStatus)
  const linkDown =
    hasUrlCheck && urlCheckPreviousStatus === "active" && BROKEN_STATUSES.includes(urlCheckStatus)
  const linkNotLive = !linkLive && !linkDown
  const linkPending = false
  const linkDisplayLabel = linkDown
    ? "Link Down"
    : linkLive
      ? "Live"
      : "Not Live"

  const { seoDa, seoDr, seoTraffic, seoSpam } = getSeoMetricsForConnection(conn)
  const benefitsLines = getBenefitLinesForConnection(conn)

  return {
    id: conn?.id ?? Math.random(),
    org,
    opportunity,
    status,
    stageNum,
    statusDate,
    costFormatted,
    sponsorship: sponsorshipNum,
    processingFee: processingFeeNum,
    processingFeePct: conn?.processing_fee_percent ?? null,
    zsFee: zsFeeNum,
    total,
    address,
    sourceUrl,
    targetUrl,
    anchor,
    paid,
    paidDate,
    method,
    linkLive,
    linkNotLive,
    linkDown,
    linkPending,
    linkDisplayLabel,
    seoDa,
    seoDr,
    seoTraffic,
    seoSpam,
    benefitsLines,
  }
}

export const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "0", label: "Created" },
  { value: "1", label: "Pending Approval" },
  { value: "2", label: "Approved by Client" },
  { value: "3", label: "Complete" },
  { value: "4", label: "Declined by Client" },
  { value: "5", label: "Bad Apple" },
]

export const LINK_STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All Link Statuses" },
  { value: "live", label: "Live" },
  { value: "not-live", label: "Not Live" },
  { value: "link-down", label: "Link Down" },
]

export const STAGE_TO_FILTER_VALUE: Record<number, string> = {
  0: "0",
  1: "1",
  2: "2",
  3: "3",
  4: "4",
  5: "5",
}
